import { defineStore } from 'pinia';
import type {
  ApplePayAccept,
  BuyAddonsModel,
  CartAcceptModel,
  PaddleBuyAddonsModel,
  PayPalAcceptModel,
  UserChangeEmailModel,
  UserInitModel,
} from '@/common/models/api.models';
import { usePaymentConfigs } from '~/stores/paymentConfigs.ts';

export const useKegelApi = defineStore('kegelApi', () => {
  const { BASE_URL, TOKEN, BRAINTREE_ID, STRIPE_ID, STRIPE_US_ID } = useRuntimeConfig().public;
  const store = useKegelStore();
  const { $versions } = useNuxtApp();
  const configsStore = usePaymentConfigs();

  class API_PAYMENTS {
    public static async post(url: string, body: any): Promise<any> {
      const parsedBody = JSON.stringify(body);

      if (!BASE_URL) {
        console.error('BASE_URL is not defined in runtimeConfig');
        throw new Error('BASE_URL is not defined in runtimeConfig');
      }

      return await fetch(`${BASE_URL}/api${url}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': TOKEN,
        },
        method: 'POST',
        body: parsedBody,
      }).then((response: Response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            const errorData = {
              ...error,
              status: response.status,
            };
            throw errorData;
          });
        }
        if (response.status === 204 || response.headers.get('Content-Length') === '0') {
          return null;
        }
        return response.json();
      }).then((data: any) => {
        return data;
      }).catch((error: unknown) => {
        throw error;
      });
    }
  }

  async function initUser(body: UserInitModel): Promise<void> {
    return await API_PAYMENTS.post('/web-payment/init-user/', { ...body, web_user_uuid: store.userUuid });
  }
  async function changeUserEmail(body: UserChangeEmailModel): Promise<boolean> {
    try {
      await API_PAYMENTS.post('/web-payment/change-email/', body);

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }
  async function paypalInit(uuid: string): Promise<{ link: string }> {
    return await API_PAYMENTS.post('/web-payment/init/pay-pal-payment/', { web_user_uuid: uuid });
  }

  async function payPalAccept(payload: PayPalAcceptModel): Promise<any> {
    return await API_PAYMENTS.post('/web-payment/accept/pay-pal-payment', payload);
  }
  async function creditCardInit(amount: number, paymentMethodType?: string): Promise<any> {
    const payload: { [key: string]: string | number | undefined } = {
      currency_code: configsStore.currentConfig[1].base.currencyCode,
      payment_method_type: paymentMethodType || 'card',
      amount,
      web_user_uuid: store.userUuid,
    };

    switch ($versions?.value.paymentForm) {
      case '2':
        payload.gateway_id = BRAINTREE_ID;
        break;
      case '4':
        payload.gateway_id = STRIPE_US_ID;
        break;
      case '1':
      default:
        payload.gateway_id = STRIPE_ID;
        break;
    }

    return await API_PAYMENTS.post('/web-payment/init/card-payment/', payload);
  }
  async function creditCardAccept(payload: CartAcceptModel | ApplePayAccept): Promise<any> {
    return await API_PAYMENTS.post('/web-payment/accept/card-payment/', { ...payload, web_user_uuid: store.userUuid });
  }

  return {
    initUser,
    paypalInit,
    payPalAccept,
    creditCardInit,
    creditCardAccept,
    changeUserEmail,
  };
});
